// Messages under the header: the error card (24.15, with "copy diagnostics"), validation
// problems, notices (saves, quota, import) and the "played on an older balance" note (24.12).
import { useState } from 'preact/hooks';
import type { Notice, OlderBalance, UiError } from '../../store';
import { template } from '../format';
import { uiText } from '../text/en';

/** Copy text to the clipboard; false where the API is missing or refused. */
export function copyText(text: string): Promise<boolean> {
  const clip = (globalThis.navigator as Navigator | undefined)?.clipboard;
  if (clip === undefined) return Promise.resolve(false);
  return clip.writeText(text).then(
    () => true,
    () => false,
  );
}

export function ErrorCard({
  error,
  title = uiText.error.title,
  onDismiss,
}: {
  error: UiError;
  title?: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const t = uiText.error;
  return (
    <div class="card card--error" role="alert">
      <p>
        <strong>{title}</strong> {t.code} <span class="num">{error.code}</span>: {error.message}
      </p>
      <div class="card__actions">
        <button
          type="button"
          class="button button--small"
          onClick={() => void copyText(error.diagnostics).then(setCopied)}
        >
          {t.copy}
        </button>
        {copied && <span class="hint" role="status">{t.copied}</span>}
        <button type="button" class="button button--small" onClick={onDismiss}>
          {t.dismiss}
        </button>
      </div>
      <details class="card__details">
        <summary>{t.show}</summary>
        <textarea class="card__diagnostics" readOnly rows={4} value={error.diagnostics} />
      </details>
    </div>
  );
}

export function Problems({ problems }: { problems: string[] }) {
  if (problems.length === 0) return null;
  return (
    <ul class="card card--problems" role="status">
      {problems.map((p, i) => (
        <li key={i}>{p}</li>
      ))}
    </ul>
  );
}

export function NoticeBar({ notice, onDismiss }: { notice: Notice; onDismiss: () => void }) {
  const text = template(uiText.notice[notice.code] ?? notice.code, { detail: notice.detail ?? '' });
  return (
    <div class="card card--notice" role="status">
      <span>{text}</span>
      <button type="button" class="button button--small" onClick={onDismiss}>
        {uiText.notice.dismiss}
      </button>
    </div>
  );
}

export function OlderBalanceNote({
  note,
  onKeep,
  onFork,
}: {
  note: OlderBalance;
  onKeep: () => void;
  onFork: () => void;
}) {
  const t = uiText.olderBalance;
  return (
    <section class="card card--notice" aria-labelledby="older-title">
      <h2 id="older-title" class="card__title">
        {t.title}
      </h2>
      {note.configDiffers && <p>{t.config}</p>}
      {note.contentDiffers && <p>{t.content}</p>}
      <div class="card__actions">
        <button type="button" class="button button--small" onClick={onKeep}>
          {t.keep}
        </button>
        <button type="button" class="button button--small" onClick={onFork}>
          {t.fork}
        </button>
      </div>
    </section>
  );
}
